import { useRef, useState, useEffect } from 'react'
import { Button } from './button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/utils'

interface HorizontalCarouselProps {
  children: React.ReactNode
  className?: string
  showNavigation?: boolean
  scrollStep?: number
}

export function HorizontalCarousel({ 
  children, 
  className,
  showNavigation = true,
  scrollStep = 300
}: HorizontalCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const checkScrollButtons = () => {
    if (!scrollRef.current) return
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
  }

  useEffect(() => {
    checkScrollButtons()
    const scrollElement = scrollRef.current
    if (scrollElement) {
      scrollElement.addEventListener('scroll', checkScrollButtons)
      return () => scrollElement.removeEventListener('scroll', checkScrollButtons)
    }
  }, [])

  // Update button visibility when hover state or scroll state changes
  useEffect(() => {
    if (!containerRef.current) return
    const buttons = containerRef.current.querySelectorAll('button')
    buttons.forEach(btn => {
      if (isHovered) {
        btn.style.opacity = '100'
      } else {
        btn.style.opacity = '0'
      }
    })
  }, [isHovered, canScrollLeft, canScrollRight])

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return
    
    const newScrollLeft = scrollRef.current.scrollLeft + (direction === 'left' ? -scrollStep : scrollStep)
    scrollRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    })
  }

  return (
    <div 
      ref={containerRef}
      className={cn("relative carousel-container", className)}
      onMouseEnter={() => {
        setIsHovered(true)
      }}
      onMouseLeave={(e) => {
        // Only hide if we're not moving to a button
        const relatedTarget = e.relatedTarget
        if (!relatedTarget || !(relatedTarget instanceof Node) || !e.currentTarget.contains(relatedTarget)) {
          setIsHovered(false)
        }
      }}
    >
      {showNavigation && canScrollLeft && (
        <Button
          variant="secondary"
          size="icon"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full shadow-lg bg-background/80 backdrop-blur-sm border border-border hover:bg-background/90 opacity-0 hover:opacity-100 transition-opacity"
          onClick={() => scroll('left')}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
      )}
      
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
      
      {showNavigation && canScrollRight && (
        <Button
          variant="secondary"
          size="icon"
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full shadow-lg bg-background/80 backdrop-blur-sm border border-border hover:bg-background/90 opacity-0 hover:opacity-100 transition-opacity"
          onClick={() => scroll('right')}
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      )}
    </div>
  )
}
